import { Warning } from '@phosphor-icons/react';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import {
  BudgetCategory,
  transactionCategoriesBaseMap,
  TransactionWithPayments,
  TruckAttributionInput,
  TruckAttributionWithTruck,
} from '../../../../../../types/apiTypes';
import { formatDate } from '../../../../../../utils/DateUtils';
import {
  getAttributionTrucks,
  getInitialSelectedTruckIds,
} from '../../../../../../utils/attributionUtils';
import { unicodeToEmoji } from '../../../../../../shared/emojiUnicode';
import { Select, Tooltip } from 'antd';
import { ClipLoader } from 'react-spinners';
import { Truck } from '../../../../../../types/dbTypes';
import { dontCategorizeCategorySet } from '../../../../../../types/constants';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../../../../utils/CurrencyUtils';
import { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { delay } from '../../../../../../utils/Delay';
import { DemoDataWrapper } from '../components/DemoDataBadge';
import toast from 'react-hot-toast';
import { useDemo } from '../../../../../../utils/DemoContext';
import TruckMultiSelect from '../../../../../shared/TruckMultiSelect';
import SplitAllocationModal from '../../../../../shared/SplitAllocationModal';

// TruckCell component - defined outside to ensure memo works correctly
interface TruckCellProps {
  transactionId: string;
  trucks: Truck[];
  isPending: boolean;
  disabled?: boolean;
  selectedTruckIds: string[];
  existingAttributions?: TruckAttributionWithTruck[];
  onSelectionChange: (transactionId: string, truckIds: string[]) => void;
  onSingleSave: (transactionId: string, truckId: string) => Promise<void>;
  onOpenSplitModal: (transactionId: string) => void;
  onAssignBusiness?: (transactionId: string) => Promise<void>;
}

const TruckCell = memo<TruckCellProps>(
  ({
    transactionId,
    trucks,
    isPending,
    disabled: forceDisabled,
    selectedTruckIds,
    existingAttributions,
    onSelectionChange,
    onSingleSave,
    onOpenSplitModal,
    onAssignBusiness,
  }) => {
    // Resolve-only trucks (e.g. a deleted truck) come off the attributions —
    // derived here from the stable `existingAttributions` prop rather than
    // passed in as a fresh array per render, so this memo'd cell doesn't miss
    // on every render.
    const additionalTrucks = useMemo(
      () => getAttributionTrucks({ truckAttributions: existingAttributions }),
      [existingAttributions]
    );

    const handleSelectionChange = useCallback(
      (truckIds: string[]) => {
        onSelectionChange(transactionId, truckIds);
      },
      [transactionId, onSelectionChange]
    );

    const handleSingleSave = useCallback(
      (selectedTruckId: string) => {
        return onSingleSave(transactionId, selectedTruckId);
      },
      [transactionId, onSingleSave]
    );

    const handleOpenSplitModal = useCallback(() => {
      onOpenSplitModal(transactionId);
    }, [transactionId, onOpenSplitModal]);

    const handleAssignBusiness = useCallback(async () => {
      await onAssignBusiness?.(transactionId);
    }, [transactionId, onAssignBusiness]);

    return (
      <div className="flex items-center">
        <TruckMultiSelect
          trucks={trucks}
          additionalTrucks={additionalTrucks}
          placeholder="Business"
          selectedTruckIds={selectedTruckIds}
          onSelectionChange={handleSelectionChange}
          onSingleSave={handleSingleSave}
          onOpenSplitModal={handleOpenSplitModal}
          onAssignBusiness={onAssignBusiness ? handleAssignBusiness : undefined}
          disabled={isPending || forceDisabled}
          isLoading={isPending}
          existingAttributions={existingAttributions}
        />
      </div>
    );
  }
);
TruckCell.displayName = 'TruckCell';

function LastTransactionsWidget({
  transactions,
  isLoading,
  addClass,
  isRecentDeposits,
  budgetCategories,
  trucks,
  loadingTrucks,
  patchTransaction,
  patchTruckAttributions,
  isPreActive = false,
}: {
  transactions: TransactionWithPayments[];
  isLoading: boolean;
  addClass?: string;
  budgetCategories: BudgetCategory[];
  trucks: Truck[];
  isRecentDeposits?: boolean;
  loadingTrucks: boolean;
  patchTransaction: (
    transaction_id: string,
    truck_id?: string,
    category?: string
  ) => Promise<void>;
  patchTruckAttributions?: (
    transaction_id: string,
    attributions: TruckAttributionInput[]
  ) => Promise<void>;
  isPreActive: boolean;
}) {
  type PendingMap = Record<string, { category?: boolean; truck?: boolean }>;
  const [pending, setPending] = useState<PendingMap>({});
  const [localCategory, setLocalCategory] = useState<Record<string, string>>(
    {}
  );

  // Multi-truck selection state
  const [selectedTruckIds, setSelectedTruckIds] = useState<
    Record<string, string[]>
  >({});

  // Split modal state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitModalTransaction, setSplitModalTransaction] =
    useState<TransactionWithPayments | null>(null);
  const [splitModalTrucks, setSplitModalTrucks] = useState<Truck[]>([]);
  const [isSavingAttributions, setIsSavingAttributions] = useState(false);

  const { screenView } = useScreenView();

  const navigate = useNavigate();
  const { isDemo } = useDemo();

  const getCategoryValue = (t: TransactionWithPayments) =>
    localCategory[t.transaction_id] ?? t.category ?? '';

  // Create a stable key based on transaction IDs to prevent infinite loops
  // Only reset local state when the actual list of transactions changes
  const transactionIds = useMemo(
    () => transactions.map((t) => t.transaction_id).join(','),
    [transactions]
  );

  useEffect(() => {
    setLocalCategory({});
    // Initialize selected truck IDs from existing transaction data
    const initialSelectedTrucks: Record<string, string[]> = {};
    transactions.forEach((t) => {
      initialSelectedTrucks[t.transaction_id] = getInitialSelectedTruckIds(t);
    });
    setSelectedTruckIds(initialSelectedTrucks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionIds]);

  const setFieldPending = useCallback(
    (id: string, field: 'category' | 'truck', value: boolean) =>
      setPending((prev) => ({
        ...prev,
        [id]: { ...prev[id], [field]: value },
      })),
    []
  );

  const onCategoryChange = useCallback(
    async (value: string, transaction_id: string) => {
      if (isPreActive) {
        toast.error('Complete your onboarding to edit transactions');
        return;
      }
      const prev = localCategory[transaction_id];
      setLocalCategory((m) => ({ ...m, [transaction_id]: value }));
      setFieldPending(transaction_id, 'category', true);
      try {
        await patchTransaction(transaction_id, undefined, value);
      } catch (e) {
        setLocalCategory((m) => ({ ...m, [transaction_id]: prev ?? '' }));
        throw e;
      } finally {
        await delay(500);
        setFieldPending(transaction_id, 'category', false);
      }
    },
    [isPreActive, localCategory, patchTransaction, setFieldPending]
  );

  // Handler for updating selected trucks in multi-select
  const handleTruckSelectionChange = useCallback(
    (transactionId: string, truckIds: string[]) => {
      setSelectedTruckIds((prev) => ({ ...prev, [transactionId]: truckIds }));
    },
    []
  );

  // Handler for saving single truck selection
  const handleSingleTruckSave = useCallback(
    async (transactionId: string, truckId: string) => {
      if (isPreActive) {
        toast.error('Complete your onboarding to edit transactions');
        return;
      }
      setFieldPending(transactionId, 'truck', true);
      try {
        await patchTransaction(transactionId, truckId, undefined);
        setSelectedTruckIds((prev) => ({
          ...prev,
          [transactionId]: [truckId],
        }));
      } catch (e) {
        throw e;
      } finally {
        await delay(500);
        setFieldPending(transactionId, 'truck', false);
      }
    },
    [isPreActive, patchTransaction, setFieldPending]
  );

  // Handler for assigning a transaction to the business (no truck). Clearing
  // attributions with an empty array tells the backend to null the truck_id.
  const handleAssignBusiness = useCallback(
    async (transactionId: string) => {
      if (!patchTruckAttributions) return;
      if (isPreActive) {
        toast.error('Complete your onboarding to edit transactions');
        return;
      }
      setFieldPending(transactionId, 'truck', true);
      try {
        await patchTruckAttributions(transactionId, []);
        setSelectedTruckIds((prev) => ({ ...prev, [transactionId]: [] }));
      } catch {
        toast.error('Failed to update truck. Please try again.');
      } finally {
        await delay(500);
        setFieldPending(transactionId, 'truck', false);
      }
    },
    [isPreActive, patchTruckAttributions, setFieldPending]
  );

  // Handler for opening split modal - accepts transactionId and looks up the transaction
  const handleOpenSplitModal = useCallback(
    (transactionId: string) => {
      if (isPreActive) {
        toast.error('Complete your onboarding to edit transactions');
        return;
      }
      const transaction = transactions.find(
        (t) => t.transaction_id === transactionId
      );
      if (!transaction) return;

      // Only use currently selected trucks from UI state
      // The modal will handle pre-fill logic based on exact match with saved attributions
      const selectedIds = selectedTruckIds[transactionId] || [];
      const selectedTruckObjects = trucks.filter((t) =>
        selectedIds.includes(t.truck_id)
      );

      setSplitModalTransaction(transaction);
      setSplitModalTrucks(selectedTruckObjects);
      setShowSplitModal(true);
    },
    [isPreActive, selectedTruckIds, trucks, transactions]
  );

  // Handler for saving truck attributions from split modal
  const handleSaveTruckAttributions = useCallback(
    async (attributions: TruckAttributionInput[]) => {
      if (!splitModalTransaction || !patchTruckAttributions) return;

      setIsSavingAttributions(true);
      try {
        await patchTruckAttributions(
          splitModalTransaction.transaction_id,
          attributions
        );
        toast.success('Truck allocations saved successfully');
        setShowSplitModal(false);
        setSplitModalTransaction(null);
        setSplitModalTrucks([]);
      } catch (error) {
        toast.error('Failed to save truck allocations');
        throw error;
      } finally {
        setIsSavingAttributions(false);
      }
    },
    [splitModalTransaction, patchTruckAttributions]
  );

  // Memoize CategoryCell to prevent unnecessary re-renders
  const CategoryCell = memo<{
    txn: TransactionWithPayments;
    budgetCategories: BudgetCategory[];
    pending: Record<string, { category?: boolean }>;
    onChange: (value: string, id: string) => void;
    screenView: string;
  }>(
    ({
      txn,
      budgetCategories,
      pending,
      onChange,
      screenView,
    }: {
      txn: TransactionWithPayments;
      budgetCategories: BudgetCategory[];
      pending: Record<string, { category?: boolean }>;
      onChange: (value: string, id: string) => void;
      screenView: string;
    }) => {
      const catVal = getCategoryValue(txn);

      const canEdit =
        txn.reversal_status == null &&
        (txn?.amount ?? 0) < 0 &&
        !dontCategorizeCategorySet.has(catVal || '');

      const catMap = Object.values(transactionCategoriesBaseMap);
      const display = catMap.find((c) => c.categoryName === catVal);

      if (!canEdit) {
        if (txn.reversal_status != null) {
          return (
            <span className="inline-block max-w-full truncate text-gray-400">
              Reversed
            </span>
          );
        }
        const title = display
          ? `${unicodeToEmoji(display.emoji_unicode)} ${display.displayName}`
          : 'Uncategorized';
        return (
          <span className="inline-block max-w-full truncate" title={title}>
            {display
              ? `${unicodeToEmoji(display.emoji_unicode)} ${display.displayName}`
              : 'Uncategorized'}
          </span>
        );
      }

      return (
        <div className="flex items-center">
          <div className="w-full">
            {(() => {
              const currentVal = catVal || '';
              const selectedOption =
                (currentVal === 'unknown'
                  ? {
                      categoryName: 'unknown',
                      emoji_unicode: 'U+2753',
                      displayName: 'Unknown',
                    }
                  : budgetCategories.find(
                      (c) => c.categoryName === currentVal
                    )) || null;

              const selectedLabel = selectedOption
                ? `${unicodeToEmoji(selectedOption.emoji_unicode)} ${selectedOption.displayName}`
                : '';

              return (
                <Tooltip
                  title={selectedLabel}
                  placement="top"
                  mouseEnterDelay={0.3}
                >
                  <Select
                    value={currentVal || undefined}
                    onChange={(v) => onChange(v, txn.transaction_id)}
                    placeholder="Select Category"
                    className={`w-full ${!catVal ? 'ant-select-status-warning' : ''}`}
                    disabled={!!pending[txn.transaction_id]?.category}
                    suffixIcon={
                      pending[txn.transaction_id]?.category ? (
                        <ClipLoader size={14} />
                      ) : undefined
                    }
                    optionLabelProp="label"
                  >
                    {[
                      ...(currentVal === 'unknown'
                        ? [
                            {
                              categoryName: 'unknown',
                              emoji_unicode: 'U+2753',
                              displayName: 'Unknown',
                            },
                          ]
                        : []),
                      ...budgetCategories,
                    ].map((cat) => (
                      <Select.Option
                        key={cat.categoryName}
                        value={cat.categoryName}
                        label={`${unicodeToEmoji(cat.emoji_unicode)} ${cat.displayName}`}
                      >
                        {/* Truncate visually in the dropdown but show full on hover */}
                        <Tooltip
                          title={`${unicodeToEmoji(cat.emoji_unicode)} ${cat.displayName}`}
                          placement="right"
                          mouseEnterDelay={0.2}
                        >
                          <div className="truncate">
                            {unicodeToEmoji(cat.emoji_unicode)}{' '}
                            {cat.displayName}
                          </div>
                        </Tooltip>
                      </Select.Option>
                    ))}
                  </Select>
                </Tooltip>
              );
            })()}
          </div>

          {!catVal && !pending[txn.transaction_id]?.category && (
            <Warning size={20} className="ml-2 text-orange-500" />
          )}
        </div>
      );
    }
  );
  CategoryCell.displayName = 'CategoryCell';

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="flex flex-col items-stretch gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                {isRecentDeposits ? 'Recent Deposits' : 'Recent Expenses'}
              </h2>
              <button
                onClick={
                  isDemo
                    ? undefined
                    : () => navigate('/dashboard/banking/transactions')
                }
                className={`text-[16px] font-[600] text-primary underline ${isDemo ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={isDemo}
              >
                Show All
              </button>
            </div>
            {!isLoading && !loadingTrucks ? (
              <div className="w-full overflow-x-auto">
                {transactions && transactions?.length > 0 ? (
                  <table className="w-full table-fixed rounded-lg bg-white shadow-md">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="w-[15%] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                          Date
                        </th>
                        <th className="w-[30%] px-1 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                          Category
                        </th>
                        <th className="w-[30%] px-1 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                          Truck
                        </th>
                        <th className="w-[25%] px-2 py-2 text-right text-[14px] font-[600] leading-[18px] text-primary">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, index) => {
                        const isReversed = txn.reversal_status != null;
                        return (
                          <tr
                            key={index}
                            className={`border-b border-gray-200 ${isReversed ? 'opacity-60' : ''}`}
                          >
                            <td className="px-2 py-2 text-[14px] text-primary">
                              {formatDate(
                                new Date(txn?.created_at ?? ''),
                                false,
                                true
                              )}
                            </td>

                            {/* CATEGORY with hover tooltips */}
                            <td className="px-1 py-2 text-[14px] text-primary">
                              <CategoryCell
                                txn={txn}
                                budgetCategories={budgetCategories}
                                pending={pending}
                                onChange={onCategoryChange}
                                screenView={screenView}
                              />
                            </td>

                            {/* TRUCK with TruckMultiSelect */}
                            <td className="px-1 py-2 text-[14px] text-primary">
                              <TruckCell
                                transactionId={txn.transaction_id}
                                trucks={trucks}
                                isPending={!!pending[txn.transaction_id]?.truck}
                                disabled={isReversed}
                                selectedTruckIds={
                                  selectedTruckIds[txn.transaction_id] ||
                                  (txn.truck_id ? [txn.truck_id] : [])
                                }
                                existingAttributions={txn.truckAttributions}
                                onSelectionChange={handleTruckSelectionChange}
                                onSingleSave={handleSingleTruckSave}
                                onOpenSplitModal={handleOpenSplitModal}
                                onAssignBusiness={
                                  patchTruckAttributions
                                    ? handleAssignBusiness
                                    : undefined
                                }
                              />
                            </td>

                            <td
                              className={`px-2 py-2 text-right text-[14px] ${
                                isReversed
                                  ? 'text-gray-400'
                                  : txn.amount && txn.amount > 0
                                    ? 'text-green-500'
                                    : 'text-primary'
                              }`}
                            >
                              <span
                                className={isReversed ? 'line-through' : ''}
                              >
                                {formatCurrency(txn?.amount ?? 0)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center">
                    <p className="text-[14px] leading-[20px] text-graysecondary">
                      No Transactions Found
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={isLoading || loadingTrucks}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="my-8 flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div className="flex flex-col items-stretch gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-3 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                {isRecentDeposits ? 'Recent Deposits' : 'Recent Expenses'}
              </h2>
              <button
                onClick={
                  isDemo
                    ? undefined
                    : () => navigate('/dashboard/banking/transactions')
                }
                className={`text-[16px] font-[600] text-primary underline ${isDemo ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={isDemo}
              >
                Show All
              </button>
            </div>
            {!isLoading && !loadingTrucks ? (
              <div className="w-full overflow-x-auto">
                {transactions && transactions?.length > 0 ? (
                  <table className="min-w-full table-auto rounded-lg bg-white shadow-md">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                          Date
                        </th>
                        <th className="px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                          Category
                        </th>
                        <th className="px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                          Truck
                        </th>
                        <th className="px-4 py-2 text-right text-[14px] font-[600] leading-[18px] text-primary">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, index) => {
                        const isReversed = txn.reversal_status != null;
                        return (
                          <tr
                            key={index}
                            className={`border-b border-gray-200 ${isReversed ? 'opacity-60' : ''}`}
                          >
                            <td className="px-2 py-2 text-[14px] text-primary">
                              {formatDate(
                                new Date(txn?.created_at ?? ''),
                                false,
                                true
                              )}
                            </td>

                            {/* CATEGORY with hover tooltips */}
                            <td className="px-2 py-2 text-[14px] text-primary">
                              <CategoryCell
                                txn={txn}
                                budgetCategories={budgetCategories}
                                pending={pending}
                                onChange={onCategoryChange}
                                screenView={screenView}
                              />
                            </td>

                            {/* TRUCK with TruckMultiSelect */}
                            <td className="px-2 py-2 text-[14px] text-primary">
                              <TruckCell
                                transactionId={txn.transaction_id}
                                trucks={trucks}
                                isPending={!!pending[txn.transaction_id]?.truck}
                                disabled={isReversed}
                                selectedTruckIds={
                                  selectedTruckIds[txn.transaction_id] ||
                                  (txn.truck_id ? [txn.truck_id] : [])
                                }
                                existingAttributions={txn.truckAttributions}
                                onSelectionChange={handleTruckSelectionChange}
                                onSingleSave={handleSingleTruckSave}
                                onOpenSplitModal={handleOpenSplitModal}
                                onAssignBusiness={
                                  patchTruckAttributions
                                    ? handleAssignBusiness
                                    : undefined
                                }
                              />
                            </td>

                            <td
                              className={`px-2 py-2 text-right text-[14px] ${
                                isReversed
                                  ? 'text-gray-400'
                                  : txn.amount && txn.amount > 0
                                    ? 'text-green-500'
                                    : 'text-primary'
                              }`}
                            >
                              <span
                                className={isReversed ? 'line-through' : ''}
                              >
                                {formatCurrency(txn?.amount ?? 0)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center">
                    <p className="text-[14px] leading-[20px] text-graysecondary">
                      No Transactions Found
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={isLoading || loadingTrucks}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="my-8 flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
      case 'mobile':
        return (
          <div className="flex flex-col items-stretch gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-3 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                {isRecentDeposits ? 'Recent Deposits' : 'Recent Expenses'}
              </h2>
              <button
                onClick={
                  isDemo
                    ? undefined
                    : () => navigate('/dashboard/banking/transactions')
                }
                className={`text-[16px] font-[600] text-primary underline ${isDemo ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={isDemo}
              >
                Show All
              </button>
            </div>
            {!isLoading && !loadingTrucks ? (
              <div className="w-full overflow-x-auto">
                {transactions && transactions?.length > 0 ? (
                  <table className="min-w-full table-auto rounded-lg bg-white shadow-md">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-1 py-2 text-left text-[12px] font-[600] leading-[18px] text-primary">
                          Category
                        </th>
                        <th className="px-1 py-2 text-left text-[12px] font-[600] leading-[18px] text-primary">
                          Truck
                        </th>
                        <th className="px-1 py-2 text-right text-[12px] font-[600] leading-[18px] text-primary">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, index) => {
                        const isReversed = txn.reversal_status != null;
                        return (
                          <tr
                            key={index}
                            className={`border-b border-gray-200 ${isReversed ? 'opacity-60' : ''}`}
                          >
                            <td className="max-w-[150px] px-1 py-2 text-[12px] text-primary">
                              <CategoryCell
                                txn={txn}
                                budgetCategories={budgetCategories}
                                pending={pending}
                                onChange={onCategoryChange}
                                screenView={screenView}
                              />
                            </td>
                            <td className="max-w-[150px] px-1 py-2 text-[12px] text-primary">
                              <TruckCell
                                transactionId={txn.transaction_id}
                                trucks={trucks}
                                isPending={!!pending[txn.transaction_id]?.truck}
                                disabled={isReversed}
                                selectedTruckIds={
                                  selectedTruckIds[txn.transaction_id] ||
                                  (txn.truck_id ? [txn.truck_id] : [])
                                }
                                existingAttributions={txn.truckAttributions}
                                onSelectionChange={handleTruckSelectionChange}
                                onSingleSave={handleSingleTruckSave}
                                onOpenSplitModal={handleOpenSplitModal}
                                onAssignBusiness={
                                  patchTruckAttributions
                                    ? handleAssignBusiness
                                    : undefined
                                }
                              />
                            </td>

                            <td
                              className={`px-1 py-2 text-right text-[12px] ${
                                isReversed
                                  ? 'text-gray-400'
                                  : txn.amount && txn.amount > 0
                                    ? 'text-green-500'
                                    : 'text-primary'
                              }`}
                            >
                              <span
                                className={isReversed ? 'line-through' : ''}
                              >
                                {formatCurrency(txn?.amount ?? 0)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center">
                    <p className="text-[14px] leading-[20px] text-graysecondary">
                      No Transactions Found
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={isLoading || loadingTrucks}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="my-8 flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <DemoDataWrapper showBadge={isPreActive} className={addClass}>
      {renderScreenView(screenView)}

      {/* Split Allocation Modal */}
      {showSplitModal &&
        splitModalTransaction &&
        !isPreActive &&
        patchTruckAttributions && (
          <SplitAllocationModal
            isOpen={showSplitModal}
            onClose={() => {
              setShowSplitModal(false);
              setSplitModalTransaction(null);
              setSplitModalTrucks([]);
            }}
            transaction={splitModalTransaction}
            selectedTrucks={splitModalTrucks}
            onSave={handleSaveTruckAttributions}
            isSaving={isSavingAttributions}
          />
        )}
    </DemoDataWrapper>
  );
}

export default LastTransactionsWidget;
